import { useEffect, useRef, useState } from 'react';
import type { TrendDay } from '../../services/analytics.api';
import { cn } from '../../utils/cn';
import { fmtCompact, niceTicks } from './ticks';

// Real pixels, no viewBox scaling: the plot is drawn at the measured width.
const DEFAULT_W = 900;
const MIN_W = 280;
const PAD_L = 44;
const PAD_R = 12;
const PICK_TOP = 8;
const PICK_H = 200;
const PICK_BASE = PICK_TOP + PICK_H;
const ORD_TOP = 256;
const ORD_H = 90;
const ORD_BASE = ORD_TOP + ORD_H;
const H = ORD_BASE + 24;
const BAR_MAX = 24;
const GAP = 2;
const LABEL_MIN_PX = 44;
const TIP_W = 176; // matches the w-44 class on the tooltip box
const TIP_GAP = 10;

const FMT_DAY = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: '2-digit' });
const FMT_FULL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
const dateOf = (day: string) => new Date(`${day}T12:00:00Z`);
const num = (v: number) => v.toLocaleString('en-US');

// Top corners rounded 4px, baseline square.
function barPath(x: number, top: number, bottom: number, w: number): string {
  const h = bottom - top;
  if (h <= 0 || w <= 0) return '';
  const r = Math.min(4, w / 2, h);
  return `M${x},${bottom}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${bottom}Z`;
}

const SERIES = {
  created: '#d1d5db',
  collected: '#2563eb',
  cancelled: '#ef4444',
  orders: '#d97706',
};

export default function TrendChart({ days }: { days: TrendDay[] }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_W);
  // Pointer and keyboard focus are kept apart so leaving the pointer never clears a focused day.
  const [hover, setHover] = useState<number | null>(null);
  const [focus, setFocus] = useState<number | null>(null);
  const hasDays = days.length > 0;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(MIN_W, Math.floor(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasDays]);

  if (!hasDays) return null;

  const n = days.length;
  const last = n - 1;
  const W = width;
  const slot = (W - PAD_L - PAD_R) / n;
  const pairW = Math.min(2 * BAR_MAX + GAP, slot * 0.8);
  const barW = (pairW - GAP) / 2;
  const cx = (i: number) => PAD_L + slot * i + slot / 2;

  // Cancelled stacks on collected, and that sum can pass created, so scale to the taller of the two.
  const pickTicks = niceTicks(Math.max(...days.map((d) => Math.max(d.created, d.collected + d.cancelled_events))));
  const ordTicks = niceTicks(Math.max(...days.map((d) => d.orders_total)));
  const pickMax = pickTicks[pickTicks.length - 1];
  const ordMax = ordTicks[ordTicks.length - 1];
  const yP = (v: number) => PICK_BASE - (v / pickMax) * PICK_H;
  const yO = (v: number) => ORD_BASE - (v / ordMax) * ORD_H;
  const oy = days.map((d) => yO(d.orders_total));
  const labelEvery = Math.max(1, Math.ceil(LABEL_MIN_PX / slot));

  const dayName = (i: number) => (i === last ? 'Today' : FMT_DAY.format(dateOf(days[i].day)));
  const shown = hover ?? focus;
  const tipIdx = shown !== null && shown < n ? shown : last; // the window can shrink while a day is active
  const tip = days[tipIdx];
  // Right of the day when it fits, otherwise left; then clamped so the box stays inside the chart.
  const tipX = cx(tipIdx);
  const tipLeft = Math.min(Math.max(tipX + TIP_GAP + TIP_W <= W ? tipX + TIP_GAP : tipX - TIP_GAP - TIP_W, 0), W - TIP_W);
  const tipRows = [
    { label: 'Created', value: tip.created, color: SERIES.created },
    { label: 'Collected', value: tip.collected, color: SERIES.collected },
    { label: 'Cancelled', value: tip.cancelled_events, color: SERIES.cancelled },
    { label: 'Orders booked', value: tip.orders_total, color: SERIES.orders },
  ];

  return (
    <div ref={wrapRef}>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-gray-300" />
          Created
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" />
          Collected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-red-500" />
          Cancelled (status history)
        </span>
        <span className="text-gray-500 sm:ml-auto">Today is still in progress</span>
      </div>

      <div className="relative" style={{ height: H }}>
        <svg width={W} height={H} className="block" role="img" aria-label="Daily pickups and orders booked">
          {shown !== null && <rect x={PAD_L + slot * tipIdx} y={0} width={slot} height={ORD_BASE} fill="#f3f4f6" />}
          {pickTicks.map((t) => (
            <g key={`p${t}`}>
              <line x1={PAD_L} x2={W - PAD_R} y1={Math.round(yP(t)) + 0.5} y2={Math.round(yP(t)) + 0.5} stroke="#f3f4f6" strokeWidth={1} />
              <text x={PAD_L - 8} y={yP(t) + 4} textAnchor="end" fontSize={11} fill="#6b7280" className="tabular-nums">
                {fmtCompact(t)}
              </text>
            </g>
          ))}
          {days.map((d, i) => {
            const x0 = cx(i) - pairW / 2;
            const x1 = x0 + barW + GAP;
            const cancelledBottom = yP(d.collected) - GAP;
            const cancelledTop = d.cancelled_events > 0 ? Math.min(yP(d.collected + d.cancelled_events), cancelledBottom - 2) : cancelledBottom;
            const created = barPath(x0, yP(d.created), PICK_BASE, barW);
            const collected = barPath(x1, yP(d.collected), PICK_BASE, barW);
            const cancelled = barPath(x1, cancelledTop, cancelledBottom, barW);
            return (
              <g key={d.day} opacity={i === last ? 0.45 : undefined}>
                {created && <path d={created} fill={SERIES.created} />}
                {collected && <path d={collected} fill={SERIES.collected} />}
                {d.cancelled_events > 0 && cancelled && <path d={cancelled} fill={SERIES.cancelled} />}
              </g>
            );
          })}
          <text x={PAD_L} y={ORD_TOP - 10} fontSize={12} fill="#6b7280">
            Orders booked
          </text>
          {ordTicks.map((t) => (
            <g key={`o${t}`}>
              <line x1={PAD_L} x2={W - PAD_R} y1={Math.round(yO(t)) + 0.5} y2={Math.round(yO(t)) + 0.5} stroke="#f3f4f6" strokeWidth={1} />
              <text x={PAD_L - 8} y={yO(t) + 4} textAnchor="end" fontSize={11} fill="#6b7280" className="tabular-nums">
                {fmtCompact(t)}
              </text>
            </g>
          ))}
          {n > 1 && (
            <polyline
              points={days.slice(0, last).map((_, i) => `${cx(i)},${oy[i]}`).join(' ')}
              fill="none"
              stroke={SERIES.orders}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          {n > 1 && (
            <line
              x1={cx(last - 1)}
              y1={oy[last - 1]}
              x2={cx(last)}
              y2={oy[last]}
              stroke={SERIES.orders}
              strokeWidth={2}
              strokeDasharray="4 4"
              strokeLinecap="round"
            />
          )}
          {days.map((d, i) => (
            <circle
              key={d.day}
              cx={cx(i)}
              cy={oy[i]}
              r={4}
              fill={i === last ? '#ffffff' : SERIES.orders}
              stroke={i === last ? SERIES.orders : '#ffffff'}
              strokeWidth={2}
            />
          ))}
          {days.map((_, i) =>
            (last - i) % labelEvery === 0 ? (
              <text key={`x${i}`} x={cx(i)} y={ORD_BASE + 16} textAnchor="middle" fontSize={11} fill="#6b7280">
                {dayName(i)}
              </text>
            ) : null
          )}
        </svg>

        <div role="list" aria-label="Daily values" className="absolute left-0 top-0" style={{ width: W, height: ORD_BASE }}>
          {days.map((d, i) => (
            <div
              key={d.day}
              role="listitem"
              tabIndex={0}
              aria-label={`${dayName(i)}: ${num(d.created)} created, ${num(d.collected)} collected, ${num(d.cancelled_events)} cancelled, ${num(d.orders_total)} orders booked`}
              onPointerEnter={() => setHover(i)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => setFocus(i)}
              onBlur={() => setFocus(null)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setHover(null);
                  setFocus(null);
                }
              }}
              className="absolute top-0 cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
              style={{ left: PAD_L + slot * i, width: slot, height: ORD_BASE }}
            />
          ))}
        </div>

        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-0 z-10 w-44 rounded-lg bg-white px-3 py-2 text-[12px] shadow-lg ring-1 ring-black/5 motion-safe:transition-opacity',
            shown === null ? 'opacity-0' : 'opacity-100'
          )}
          style={{ left: tipLeft }}
        >
          <div className="mb-1 font-medium text-gray-900">
            {FMT_FULL.format(dateOf(tip.day))}
            {tipIdx === last ? ' · in progress' : ''}
          </div>
          {tipRows.map((r) => (
            <div key={r.label} className="flex items-center gap-2 tabular-nums">
              <span className="h-[2px] w-3 shrink-0 rounded-full" style={{ background: r.color }} />
              <span className="font-semibold text-gray-900">{num(r.value)}</span>
              <span className="text-gray-500">{r.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
